/*
 * Purpose: LinkedIn OAuth (OIDC) — connect member profile for job-hunt flows
 * Last modified: 2026-06-25 | Agent
 * Completeness: 85
 */
import { useSecretStore } from '@/stores/secretStore';
import { resolveSecret } from '@/lib/resolveSecret';

const LINKEDIN_AUTH = 'https://www.linkedin.com/oauth/v2/authorization';
const OAUTH_STATE_KEY = 'autoblocks_linkedin_oauth_state';
const DEFAULT_SCOPES = 'openid profile email';

export function getLinkedInClientId(): string | null {
  return resolveSecret('LINKEDIN_CLIENT_ID');
}

export function getLinkedInClientSecret(): string | null {
  return resolveSecret('LINKEDIN_CLIENT_SECRET');
}

export function hasLinkedInOAuthConfig(): boolean {
  return Boolean(getLinkedInClientId() && getLinkedInClientSecret());
}

export function getLinkedInRedirectUri(): string {
  const fromVault = resolveSecret('LINKEDIN_REDIRECT_URI');
  if (fromVault) return fromVault;
  if (typeof window !== 'undefined') {
    return `${window.location.origin}/oauth/linkedin/callback`;
  }
  return 'http://localhost:3000/oauth/linkedin/callback';
}

export function getLinkedInAccessToken(): string | null {
  return resolveSecret('LINKEDIN_TOKEN');
}

export function isLinkedInConnected(): boolean {
  return Boolean(getLinkedInAccessToken());
}

export function startLinkedInOAuth(returnPath = '/compose'): void {
  const clientId = getLinkedInClientId();
  if (!clientId) {
    throw new Error('Add LINKEDIN_CLIENT_ID to Secrets vault.');
  }
  if (!getLinkedInClientSecret()) {
    throw new Error('Add LINKEDIN_CLIENT_SECRET to Secrets — required for OAuth token exchange.');
  }

  const state = `${randomState()}:${returnPath.replace(/^\//, '')}`;
  sessionStorage.setItem(OAUTH_STATE_KEY, state);

  const redirectUri = getLinkedInRedirectUri();
  const params = new URLSearchParams({
    response_type: 'code',
    client_id: clientId,
    redirect_uri: redirectUri,
    scope: DEFAULT_SCOPES,
    state,
  });

  window.location.assign(`${LINKEDIN_AUTH}?${params.toString()}`);
}

function randomState(): string {
  const arr = new Uint8Array(16);
  crypto.getRandomValues(arr);
  return Array.from(arr, (b) => b.toString(16).padStart(2, '0')).join('');
}

export async function exchangeLinkedInCode(code: string): Promise<{
  access_token: string;
  expires_in?: number;
}> {
  const clientId = getLinkedInClientId();
  const clientSecret = getLinkedInClientSecret();
  if (!clientId || !clientSecret) {
    throw new Error('LinkedIn Client ID and Client Secret must be in Secrets.');
  }

  const redirectUri = getLinkedInRedirectUri();
  const res = await fetch('/api/oauth/linkedin/token', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      code,
      redirect_uri: redirectUri,
      client_id: clientId,
      client_secret: clientSecret,
    }),
  });

  if (!res.ok) {
    const err = (await res.json().catch(() => ({}))) as { error?: string; message?: string };
    throw new Error(err.error || err.message || `Token exchange failed (${res.status})`);
  }

  return res.json() as Promise<{ access_token: string; expires_in?: number }>;
}

export function persistLinkedInToken(accessToken: string): void {
  const store = useSecretStore.getState();
  void store.setSecret('LINKEDIN_TOKEN', accessToken);
  void store.setSecret('LINKEDIN_ACCESS_TOKEN', accessToken);
}

export function disconnectLinkedInOAuth(): void {
  const store = useSecretStore.getState();
  for (const name of ['LINKEDIN_TOKEN', 'LINKEDIN_ACCESS_TOKEN']) {
    const existing = store.secrets.find((s) => s.name === name);
    if (existing) store.deleteSecret(existing.id);
  }
}

export function parseOAuthState(state: string | null): { valid: boolean; returnPath: string } {
  const expected = sessionStorage.getItem(OAUTH_STATE_KEY);
  sessionStorage.removeItem(OAUTH_STATE_KEY);
  if (!state || !expected || state !== expected) {
    return { valid: false, returnPath: '/compose' };
  }
  const slash = state.indexOf(':');
  const returnPath = slash >= 0 ? `/${state.slice(slash + 1)}` : '/compose';
  return { valid: true, returnPath };
}
